import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { SettledContent, SETTLED_STATE } from '@/shared/ui/animations';
import { EmptyStateTitle } from '@/shared/ui';
import {
  PROJECT_CAMPAIGNS_PAGE_SIZE,
  projectCampaignsQueryOptions,
  projectPerformanceQueryOptions,
} from '@/entities/media-campaign';
import { usePerformanceFilters } from '@/features/performance-filters';
import { ScopeBreadcrumb, useScope } from '@/features/performance-scope';
import {
  PerformanceDashboard,
  previousWindow,
  type TrendMetric,
} from '@/widgets/performance-dashboard';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { track } from '@/shared/analytics';
import { ROUTES } from '@/shared/config/routes';

/**
 * Performance → Overview.
 *
 * Three states are kept apart deliberately: loading, no campaigns linked, and the
 * request failed. A failed request rendering as an empty dashboard is the specific
 * mistake this section must not repeat — it reads as "this project delivered nothing",
 * which is a different and false statement.
 */
export function Overview() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const projectId = project.platformProjectId;
  const { filters } = usePerformanceFilters(projectId);
  const { scope, setScope } = useScope(projectId);
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('impressions');

  const current = useQuery(
    projectPerformanceQueryOptions(projectId, {
      startDate: filters.range.startDate,
      endDate: filters.range.endDate,
      platforms: filters.platforms,
      channels: filters.channels,
      campaignId: scope.campaignId ?? undefined,
    }),
  );

  const prevRange = useMemo(
    () => previousWindow(filters.range.startDate, filters.range.endDate),
    [filters.range.startDate, filters.range.endDate],
  );

  const previous = useQuery({
    // Same platforms/channels/scope as `current`: comparing a filtered or scoped window
    // against an unfiltered one would produce a delta between two different scopes, not
    // a trend.
    ...projectPerformanceQueryOptions(projectId, {
      ...prevRange,
      platforms: filters.platforms,
      channels: filters.channels,
      campaignId: scope.campaignId ?? undefined,
    }),
    // Only asked for when the comparison is on: it doubles the request count and
    // nothing renders it otherwise.
    // Always requested: the deltas are part of reading the window, not an option.
    enabled: true,
  });

  // The project's campaign list, not the performance breakdown, answers both "what is this
  // campaign called" and "does it belong here". The breakdown is keyed by whichever
  // dimension was requested and is narrowed by the same filters as the numbers, so it can
  // do neither: it cannot name a campaign it is not keyed by, and an empty one means "no
  // delivery matched" rather than "no such campaign". Pacing already reads this list, so
  // all three views now name a scope the same way.
  const campaigns = useQuery(projectCampaignsQueryOptions(projectId));
  const projectCampaigns = campaigns.data;

  const campaignName = useMemo(
    () =>
      scope.campaignId
        ? projectCampaigns?.find((campaign) => campaign.id === scope.campaignId)?.name
        : undefined,
    [scope.campaignId, projectCampaigns],
  );

  // Only provable while the page came back short: a project deeper than one page can hold
  // the scoped campaign without it appearing in this list, and telling a marketer their
  // link is broken is too strong a claim to make on a maybe.
  const scopeIsAbsent =
    scope.campaignId !== null &&
    projectCampaigns !== undefined &&
    campaignName === undefined &&
    projectCampaigns.length < PROJECT_CAMPAIGNS_PAGE_SIZE;

  // In a useEffect keyed on isError, not the render body: this component re-renders
  // on every filter and trend-metric change, and React skips the effect while
  // isError stays the same, so the same failed query is reported exactly once.
  useEffect(() => {
    if (!current.isError) return;
    track('feature_error_displayed', {
      // eslint-disable-next-line i18next/no-literal-string -- analytics enum, not copy
      feature_area: 'performance',
      error_code: 'performance_load_failed',
    });
  }, [current.isError]);

  // One crossfade for the whole answer, so the charts and the invitation to add
  // a campaign never dissolve into an identical copy of themselves.
  const viewState = current.isError
    ? SETTLED_STATE.error
    : projectCampaigns?.length === 0 ||
        scopeIsAbsent ||
        (!current.isLoading && current.data?.campaignCount === 0)
      ? SETTLED_STATE.empty
      : SETTLED_STATE.rows;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <ScopeBreadcrumb scope={scope} campaignName={campaignName} onWiden={setScope} />

      <SettledContent
        state={viewState}
        className="min-h-0 flex-1"
        contentClassName="flex min-h-0 flex-1 flex-col gap-4"
      >
        {current.isError ? (
          <Empty role="alert" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceOverview.couldNotLoadThisProjectsPerformance')}
              </EmptyStateTitle>
              <EmptyDescription>
                {current.error instanceof Error
                  ? current.error.message
                  : t('projectDetail.performanceOverview.theRequestDidNotComeBack')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={() => void current.refetch()}>
                {t('projectDetail.performanceOverview.tryAgain')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : projectCampaigns?.length === 0 ? (
          // Ahead of every scope-aware branch, and read from the campaign list rather than
          // the response: an empty project is empty whether or not a `?campaign=` link
          // pointed at it, and that reader needs the CTA below, not a story about their link.
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceOverview.noCampaignsYet')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceOverview.thisDashboardReportsTheCampaigns')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" asChild>
                <Link to={ROUTES.PROJECT_PERFORMANCE_CAMPAIGNS(projectId)}>
                  {t('projectDetail.performanceOverview.addACampaign')}
                </Link>
              </Button>
            </EmptyContent>
          </Empty>
        ) : scopeIsAbsent ? (
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceOverview.thisCampaignIsntInThisProject')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceOverview.theCampaignThisLinkPointsTo')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : !current.isLoading && current.data?.campaignCount === 0 ? (
          // Reached only once the project is known to have campaigns and the scope is known
          // to name one of them, so an empty response is the filters' doing, not the link's.
          // The server counts what survived `campaignId` AND `platforms` AND `channels`, so
          // it cannot tell those apart on its own.
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performanceOverview.nothingDeliveredInThisView')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performanceOverview.noCampaignMatchesTheseFilters')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <PerformanceDashboard
            performance={current.data}
            previous={previous.data}
            trendMetric={trendMetric}
            onTrendMetricChange={(metric) => {
              setTrendMetric(metric);
              track('performance_trend_metric_switched', { project_id: projectId, metric });
            }}
            isLoading={current.isLoading}
          />
        )}
      </SettledContent>
    </div>
  );
}
